/*
 * Byte-identical in Cortex and Quantum.
 *
 * AUDIENCE PLANNING ON THE STRATEGY PAGE: THE CAMPAIGN'S OWN WORDS, PRESENTED.
 *
 * The backend's audienceIntelligence section lists the planner's saved
 * Interests and Custom Targeting entries in saved order, each with a priority
 * label, a planning weight and the planner field it came from. Every entry is
 * the campaign's own saved text (utils/campaignOwnWords.js). This module
 * decides what each entry IS and how a card presents it — without rewording
 * it, shortening it past its meaning, inventing a rationale, or naming any
 * campaign.
 *
 * Why these rules exist (campaign 55, 2026-09-30): the planner saved its
 * Interests and Custom Targeting fields as paragraphs. The backend reads a
 * list out of a string by splitting on commas, so one sentence of proposed
 * content themes arrived as four "audiences", one of them "and Vanderbilt
 * football. Dallas — Dallas Cowboys", and an instruction ("Build prospecting
 * audiences using …") arrived beside two of its own comma-separated pieces.
 * The page then wrapped every entry in template sentences and called all of
 * it "Ranked Target Cohorts". A first correction gave each entry a short
 * heading; the review (2026-10-01) found that a shortened heading can drop
 * the words that carry an entry's meaning ("Proposed …", "Exclude: …") and
 * that a piece of a note, shortened, reads as a recommendation.
 *
 * What an entry is (category), read from its own words and its planner field:
 *   fragment     a piece of a longer saved note: a leading conjunction, or
 *                two sentences joined by the comma split
 *   exclusion    "Exclude: …", "Do not target …", "Avoid …"
 *   condition    "Subject to …", "Pending …", "Only if …", "Requires …"
 *   theme        "Proposed …", or a descriptor about themes, content or
 *                creative ("Proposed local content themes: X")
 *   instruction  an imperative or a "do not …" sentence
 *   custom       a Custom Targeting entry with none of the above: a targeting
 *                note, not a verified audience segment
 *   audience     an Interest Targeting entry with none of the above, or the
 *                planner default
 *   note         a list carrying one of the markers above, or an entry whose
 *                planner field is unknown — ambiguous, so a note
 * Only `audience` counts as an audience group. A section whose entries are
 * all audiences is framed as "Priority Audiences"; any other mix is framed as
 * "Audience Planning" with a count of planning inputs.
 *
 * What a card shows:
 *   name         the entry verbatim when it fits a heading (so "Proposed …"
 *                stays proposed and "Exclude: …" stays an exclusion); for an
 *                audience list, its first two items joined by "and"; for a
 *                longer entry, a neutral label for what it is, with the entry
 *                itself beneath; a fragment is always a "Planner note", never
 *                a heading cut at a guessed boundary.
 *   description  the saved entry verbatim whenever the name is not the whole
 *                entry (an interest list introduced as the audiences
 *                interested in it); a backend-written description that is
 *                not retired template copy is shown as served.
 *   note         a backend-written rationale that is not retired template
 *                copy, or the one qualification this module adds: a fragment
 *                says it is part of a longer planner entry.
 *   labels       the planner field, and the category where the heading does
 *                not already say it.
 *   weight       the share, labelled as a planning weight. The backend assigns
 *                it from the planner field and entry order (42, 34, 26, 20 for
 *                interests; 34, 28, 22 for custom targeting, normalised); the
 *                section says so in one visible sentence.
 *
 * Retired template copy (the backend no longer writes it; older payloads and
 * saved fixtures still carry it) is recognised by its fixed wording and never
 * rendered.
 */

const FRAGMENT_NOTE = "Part of a longer planner entry, shown as saved.";
const INTEREST_LIST_LEAD = "Audiences interested in ";
const VERBATIM_HEADING_WORD_LIMIT = 12;
const LIST_NAME_WORD_LIMIT = 8;

export const AUDIENCE_PLANNING_WEIGHT_LABEL = "Planning weight";
export const AUDIENCE_PLANNING_WEIGHT_DISCLOSURE =
  "Planning weights are assigned automatically from each entry's planner field and its position in the saved order. They are not measured audience size, reach, affinity, conversion likelihood, or budget allocation.";
export const AUDIENCE_PLANNING_WEIGHT_CHART_NOTE =
  "Assigned from the planner field and entry order, not measured.";

export const AUDIENCE_SECTION_FRAMING = Object.freeze({
  audiences: Object.freeze({
    title: "Priority Audiences",
    subtitle: "Recommended audience groups and their role in the campaign.",
  }),
  planning: Object.freeze({
    title: "Audience Planning",
    subtitle: "Interests and content themes informing the campaign.",
  }),
});

const RETIRED_TEMPLATE_COPY = Object.freeze([
  /\bsignals strong thematic affinity and gives the campaign a clear top-funnel audience anchor\.?$/i,
  /\bcues to turn broad title interest into qualified traffic\.?$/i,
  /\bacts as a high-intent behavioral or contextual segment inside the approved plan\.?$/i,
  /^this cohort is useful for mid-funnel reinforcement and higher-conversion sequencing once awareness is established\.?$/i,
  /^sherloc is prioritizing multiple cohort lanes so reach, qualification, and conversion pressure can ladder cleanly\.?$/i,
  /^sherloc is currently operating with one broad core audience lane until more granular cohorts are saved\.?$/i,
  /^cohort rationale pending final targeting notes\.?$/i,
  /^audience strategy details will appear once targeting inputs are finalized\.?$/i,
]);

const SOURCE_LABELS = Object.freeze({
  "interest targeting": "Interest targeting",
  "custom targeting": "Custom targeting",
  "planner fallback": "Planner default",
});

const CATEGORY_LABELS = Object.freeze({
  audience: "Audience interest",
  custom: "Custom targeting",
  theme: "Proposed content theme",
  instruction: "Planning instruction",
  exclusion: "Exclusion",
  condition: "Condition",
  fragment: "Planner note",
  note: "Planner note",
  empty: "",
});

const LEADING_CONJUNCTION = /^(?:and|or|but|nor)\s+/i;
// A sentence end followed by another sentence: two saved sentences in one entry.
const SENTENCE_BOUNDARY = /[.!?]\s+(?=[A-Z0-9"“(])/;
// "Descriptor: value" — a short descriptor with no sentence punctuation of its own.
const DESCRIPTOR = /^([^:;.!?]{3,80}?):\s+(.+)$/;
const EXCLUSION =
  /^(?:exclude|excluding|exclusions?|exclusion list|do not target|don't target|never target|avoid|suppress|suppression|negative(?:ly)?\s+target(?:ing)?|block)\b/i;
const CONDITION =
  /^(?:subject to|pending|conditional(?:ly)? (?:on|upon)|contingent (?:on|upon)|only (?:if|when|where|with|after)|if|unless|requires?|requiring|provided that|where (?:supported|available|permitted|eligible|allowed))\b/i;
const PROPOSED = /^(?:proposed|potential|possible|suggested|candidate|tentative|draft)\b/i;
const THEME_WORDS = /\b(?:themes?|content|creative|messag(?:e|es|ing)|storylines?|angles?|topics?|narratives?)\b/i;
const INSTRUCTION =
  /^(?:build|create|develop|use|utili[sz]e|target|test|evaluate|consider|prioriti[sz]e|keep|apply|confirm|validate|report|distinguish|maintain|match|retarget|expand|layer|run|launch|pause|limit|cap|focus|lean|treat|measure|track|review|assess|explore|leverage|include|add|remove|drop|set|hold|reserve|allocate|shift|scale|optimi[sz]e|sequence|rotate|balance|ensure|start|begin|extend|deliver|serve|reach|activate|deploy|place|position|plan|prepare|schedule|split|weight|monitor|flag|verify|check|separate|combine|pair|align|adjust|refine|reduce|increase|grow|introduce|roll out|phase|stage|pilot|trial|do not|don't|never)\b/i;

const normalizeWhitespace = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

// Text a card may show: strings and finite numbers only, never a stringified
// object, and never the placeholders a bad serialization leaves behind.
export const cleanAudienceGroupText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value !== "string") return "";
  const text = normalizeWhitespace(value);
  if (!text || /^(?:\[object (?:object|promise)\]|undefined|null|nan)$/i.test(text)) return "";
  return text.replace(/\[object\s+(?:object|promise)\]/gi, "").replace(/\s{2,}/g, " ").trim();
};

const trimTerminal = (text) => normalizeWhitespace(text).replace(/[\s.,:;]+$/u, "").trim();
const words = (text) => normalizeWhitespace(text).split(" ").filter(Boolean);
const countWords = (text) => words(text).length;
const firstWords = (text, limit) => trimTerminal(words(text).slice(0, limit).join(" "));

const splitItems = (text, separator) =>
  text
    .split(separator)
    .map((item) => trimTerminal(normalizeWhitespace(item).replace(LEADING_CONJUNCTION, "")))
    .filter(Boolean);

const hasMeaningMarker = (text) =>
  EXCLUSION.test(text) || CONDITION.test(text) || PROPOSED.test(text) || INSTRUCTION.test(text);

const sourceKey = (source) => cleanAudienceGroupText(source).toLowerCase();

export const isRetiredAudienceTemplateCopy = (value) => {
  const text = cleanAudienceGroupText(value);
  return Boolean(text) && RETIRED_TEMPLATE_COPY.some((pattern) => pattern.test(text));
};

export const formatAudienceGroupSourceLabel = (source) => {
  const key = sourceKey(source);
  if (!key) return "";
  return SOURCE_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

const pluralCount = (count, singular, plural) => {
  if (count === null || count === undefined || (typeof count === "string" && !count.trim())) return "";
  const numeric = Math.floor(Number(count));
  if (!Number.isFinite(numeric) || numeric < 0) return "";
  return `${numeric} ${numeric === 1 ? singular : plural}`;
};

export const formatAudienceGroupCount = (count) => pluralCount(count, "audience group", "audience groups");
export const formatPlanningInputCount = (count) => pluralCount(count, "planning input", "planning inputs");

// The share as the page shows it: the backend's one-decimal value, never
// re-rounded into a different number than the card's.
export const formatPlanningWeight = (value) => {
  if (value === null || value === undefined || (typeof value === "string" && !value.trim())) return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return `${Number.isInteger(numeric) ? numeric.toFixed(0) : numeric.toFixed(1)}%`;
};

/*
 * What a saved entry is, and the list items it carries when it is a list.
 */
export const classifyAudienceGroupEntry = (value, source) => {
  const label = cleanAudienceGroupText(value);
  if (!label) return { category: "empty", items: [], descriptor: null };

  const key = sourceKey(source);
  const semicolonItems = label.includes(";") ? splitItems(label, ";") : [];
  const items = semicolonItems.length >= 2 ? semicolonItems : [];
  const descriptor = label.match(DESCRIPTOR);
  const descriptorText = descriptor && countWords(descriptor[1]) <= 6 ? descriptor[1] : null;

  if (LEADING_CONJUNCTION.test(label) || SENTENCE_BOUNDARY.test(label)) {
    return { category: "fragment", items, descriptor: descriptorText };
  }
  if (EXCLUSION.test(label)) return { category: "exclusion", items, descriptor: descriptorText };
  if (CONDITION.test(label)) return { category: "condition", items, descriptor: descriptorText };
  if (PROPOSED.test(label) || (descriptorText && THEME_WORDS.test(descriptorText))) {
    return { category: "theme", items, descriptor: descriptorText };
  }
  if (INSTRUCTION.test(label)) return { category: "instruction", items, descriptor: descriptorText };
  if (items.length && items.some((item) => hasMeaningMarker(item))) {
    return { category: "note", items, descriptor: descriptorText };
  }
  if (key === "custom targeting") return { category: "custom", items, descriptor: descriptorText };
  if (key === "interest targeting" || key === "planner fallback") {
    return { category: "audience", items, descriptor: descriptorText };
  }
  return { category: "note", items, descriptor: descriptorText };
};

export const formatAudienceCategoryLabel = (category) => CATEGORY_LABELS[category] || "";

function listName(items) {
  const pair = items.slice(0, 2).join(" and ");
  if (items.length >= 2 && countWords(pair) <= LIST_NAME_WORD_LIMIT) return pair;
  return firstWords(items[0] || "", LIST_NAME_WORD_LIMIT);
}

/*
 * The card for one served cohort. `description` and `note` carry their origin
 * so the page can word template copy for the advertiser's category while
 * leaving the campaign's own saved text exactly as saved:
 *   served        written by the backend for this campaign
 *   saved         the campaign's own entry (with, for an interest list, the
 *                 lead-in that says what interest targeting means)
 *   presentation  this module's one qualification
 */
export const describeAudienceGroup = (cohort) => {
  const safe = cohort && typeof cohort === "object" && !Array.isArray(cohort) ? cohort : {};
  const label = cleanAudienceGroupText(safe.label);
  const { category, items } = classifyAudienceGroupEntry(label, safe.source);
  const sourceLabel = formatAudienceGroupSourceLabel(safe.source);
  const categoryLabel = formatAudienceCategoryLabel(category);
  const countsAsAudience = category === "audience";

  // The heading: the entry itself when it fits, so no word that carries its
  // meaning is dropped; a list of interests by its first two items; otherwise
  // a neutral label for what the entry is. A fragment never gets a heading cut
  // at a guessed boundary.
  let name = "";
  let nameIsLabel = false;
  if (category === "empty") {
    name = "";
  } else if (category === "fragment") {
    name = categoryLabel;
    nameIsLabel = true;
  } else if (category === "audience" && items.length >= 2) {
    name = listName(items);
  } else if (countWords(label) <= VERBATIM_HEADING_WORD_LIMIT) {
    name = trimTerminal(label);
  } else {
    name = categoryLabel;
    nameIsLabel = true;
  }
  const nameIsWholeEntry = Boolean(name) && !nameIsLabel && trimTerminal(name) === trimTerminal(label);

  const servedDescription = isRetiredAudienceTemplateCopy(safe.description)
    ? ""
    : cleanAudienceGroupText(safe.description);
  const servedRationale = isRetiredAudienceTemplateCopy(safe.rationale)
    ? ""
    : cleanAudienceGroupText(safe.rationale);
  const descriptionEchoesLabel =
    Boolean(servedDescription) && trimTerminal(servedDescription).toLowerCase() === trimTerminal(label).toLowerCase();

  let description = "";
  let descriptionOrigin = "";
  if (servedDescription && !descriptionEchoesLabel) {
    description = servedDescription;
    descriptionOrigin = "served";
  } else if (label && category !== "empty" && !nameIsWholeEntry) {
    description =
      category === "audience" && items.length >= 2 && sourceKey(safe.source) === "interest targeting"
        ? `${INTEREST_LIST_LEAD}${label}`
        : label;
    descriptionOrigin = "saved";
  }

  let note = "";
  let noteOrigin = "";
  if (servedRationale) {
    note = servedRationale;
    noteOrigin = "served";
  } else if (category === "fragment") {
    note = FRAGMENT_NOTE;
    noteOrigin = "presentation";
  }

  // The topline labels: the planner field, then the category where the
  // heading does not already say it.
  const labels = [];
  if (sourceLabel) labels.push(sourceLabel);
  if (
    category !== "audience" &&
    category !== "custom" &&
    category !== "empty" &&
    categoryLabel &&
    !nameIsLabel &&
    categoryLabel.toLowerCase() !== sourceLabel.toLowerCase()
  ) {
    labels.push(categoryLabel);
  }

  return {
    category,
    categoryLabel,
    countsAsAudience,
    name,
    nameIsLabel,
    description,
    descriptionOrigin,
    note,
    noteOrigin,
    sourceLabel,
    labels,
  };
};

/*
 * How the section introduces itself: as recommended audience groups only when
 * every entry is one; otherwise as audience planning inputs.
 */
export const frameAudienceSection = (groups = []) => {
  const list = Array.isArray(groups) ? groups.filter((group) => group && typeof group === "object") : [];
  const audienceCount = list.filter((group) => group.countsAsAudience).length;
  const mode = list.length > 0 && audienceCount === list.length ? "audiences" : "planning";
  const framing = AUDIENCE_SECTION_FRAMING[mode];
  return {
    mode,
    title: framing.title,
    subtitle: framing.subtitle,
    badge: mode === "audiences" ? formatAudienceGroupCount(list.length) : formatPlanningInputCount(list.length),
    count: list.length,
    audienceCount,
  };
};

// The priority chart names each row the way its card is headed; a row whose
// card is headed by a neutral label is told apart by its position.
export const formatAudienceGroupChartLabel = (group, rank) => {
  if (!group || typeof group !== "object") return "";
  if (group.nameIsLabel && Number.isFinite(Number(rank))) return `${group.name} · entry ${Number(rank)}`;
  return group.name || "";
};

export const AUDIENCE_GROUP_FRAGMENT_NOTE = FRAGMENT_NOTE;
export const AUDIENCE_GROUP_INTEREST_LIST_LEAD = INTEREST_LIST_LEAD;

/*
 * PLANNING WEIGHTS UNDER PRESENTATION CONTRACT V2 (normalizedPlanningWeights).
 *
 * Campaign 55 (2026-10-01): the Planning Weights chart above showed a saved
 * fragment as "Planner note · entry 4", an instruction as "Planning
 * instruction · entry 5", college football twice and one of three approved
 * markets, every weight assigned by planner field and entry position. Under
 * the v2 contract the backend serves the section's planning weights as a
 * normalized model (backend utils/strategyPlanningWeights.js): the planner
 * inputs read as strategic concepts (one concept however often it is saved,
 * every approved market reconciled, conditions and tactics left out), grouped
 * into planning priorities and weighted by planning tier, never by position.
 *
 * The page renders that model as served. Nothing here re-derives a concept or
 * re-rounds a weight. These rules only refuse what must never reach the page
 * as a planning-weight label (a planner field name, an entry position, a key,
 * a placeholder) and word the model's market coverage, so a market the
 * planner inputs leave without a local signal is said on the page, never
 * silently missing. A model with any row the page cannot show, or whose
 * weights do not total 100.0, is withheld whole: the page never draws a
 * partial chart.
 *
 * The coverage is said for the reader: a client reads that the market's local
 * planning is still being finalized; the operator (Quantum) reads the review
 * flag itself. The page names its reader (`audience`).
 */
export const PLANNING_WEIGHTS_CONTRACT_VERSION = "strategy_planning_weights_v1";
export const PLANNING_WEIGHTS_DISCLOSURE =
  "Strategic emphasis derived from the approved campaign brief and planner inputs. Weights indicate relative planning priority, not measured performance.";
export const PLANNING_WEIGHTS_CHART_META = "Relative planning priority";

const PLANNING_CATEGORY_LABELS = Object.freeze({
  core_affinity: "Core affinity",
  local_market_affinity: "Local market affinity",
  content_context: "Content context",
  audience_test: "Audience tests",
});
const PLANNING_PRIORITY_LABELS = Object.freeze(["Primary", "Secondary", "Support", "Test"]);
export const PLANNING_WEIGHTS_AUDIENCES = Object.freeze({ CLIENT: "client", OPERATOR: "operator" });
const PLANNING_WEIGHTS_EMPTY_COPY = Object.freeze({
  client: Object.freeze({
    no_concepts: "Audience planning priorities are still being finalized.",
    withheld: "Audience planning priorities are still being finalized.",
    default: "Audience planning inputs will appear once the planner's targeting is saved.",
  }),
  operator: Object.freeze({
    no_concepts: "Strategy review: no planning priority could be derived from the planner inputs.",
    withheld: "Strategy review: the planning-weights model could not be shown as served.",
    default: "Audience planning inputs will appear once the planner's targeting is saved.",
  }),
});

// A planner field name, an entry position, a key, a placeholder.
const INTERNAL_PLANNING_WORDING =
  /\bentry\s*#?\d+\b|\bplanner\s+(?:note|field|default|entry)s?\b|\bplanning\s+instructions?\b|\b(?:interest|custom)[\s_-]*targeting\b|\bsource\s+keys?\b|\[object\b|\b(?:undefined|null|nan)\b/i;
const IDENTIFIER_TEXT = /\b[a-z0-9]+_[a-z0-9_]+\b|\b[a-z]{3,}(?:[A-Z][a-z0-9]+)+\b|[{}[\]\\]/;

export const isInternalPlanningLabel = (value) => {
  const text = cleanAudienceGroupText(value);
  return !text || INTERNAL_PLANNING_WORDING.test(text) || IDENTIFIER_TEXT.test(text);
};

const readPlanningWeightRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const displayLabel = cleanAudienceGroupText(row.displayLabel);
  const categoryLabel = PLANNING_CATEGORY_LABELS[row.category];
  const weight = typeof row.weight === "number" ? row.weight : Number.NaN;
  if (!displayLabel || isInternalPlanningLabel(displayLabel) || !categoryLabel) return null;
  if (!Number.isFinite(weight) || weight < 0 || weight > 100) return null;
  // The first spelling of each member, compared in lower case, in one pass.
  const members = [];
  const seen = new Set([displayLabel.toLowerCase()]);
  for (const member of Array.isArray(row.members) ? row.members : []) {
    const label = cleanAudienceGroupText(member?.displayLabel);
    if (!label || isInternalPlanningLabel(label) || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    members.push(label);
  }
  const note = cleanAudienceGroupText(row.note);
  return {
    key: cleanAudienceGroupText(row.key) || displayLabel,
    category: row.category,
    categoryLabel,
    displayLabel,
    members,
    // The chart names a market's or a concept's own signals beside it; a
    // priority that holds several concepts (the content context, the audience
    // tests, a folded list) lists them on its card only.
    chartDetail: row.aggregate === true ? "" : members.join(", "),
    note: note && !isInternalPlanningLabel(note) ? note : "",
    weight,
    priorityLabel: PLANNING_PRIORITY_LABELS.includes(row.priorityLabel) ? row.priorityLabel : "",
  };
};

// The page's sentence for each approved market the planner inputs leave
// without a local signal, worded for its reader; and, for the operator only,
// each local-theme header that names no approved market.
const planningCoverageNotices = (coverage, audience) => {
  const safe = coverage && typeof coverage === "object" && !Array.isArray(coverage) ? coverage : {};
  const shown = (entries) =>
    [...new Set((Array.isArray(entries) ? entries : []).map((entry) => cleanAudienceGroupText(entry?.label)))].filter(
      (label) => label && !isInternalPlanningLabel(label)
    );
  const missing = shown((Array.isArray(safe.markets) ? safe.markets : []).filter((market) => market?.status === "missing"));
  if (audience !== PLANNING_WEIGHTS_AUDIENCES.OPERATOR) {
    return missing.map((label) => `Local planning priorities for ${label} are still being finalized.`);
  }
  return [
    ...missing.map((label) => `Strategy review: ${label} is an approved market, but the planner inputs name no local signal for it.`),
    ...shown(safe.unmatchedLocalHeaders).map(
      (label) => `Strategy review: the local themes name ${label}, which is not an approved market.`
    ),
  ];
};

// What the backend withheld as excluded although it may not be: an item that
// follows an exclusion in a saved list whose line breaks are lost, an item
// within a list an exclusion ends ("within", quoting that list), or an item
// whose own words carry a negation ("self"). Never shown as a target, and the
// operator's to confirm. The client page never words it.
const exclusionReviewNotices = (review, audience) => {
  if (audience !== PLANNING_WEIGHTS_AUDIENCES.OPERATOR) return [];
  const safe = review && typeof review === "object" && !Array.isArray(review) ? review : {};
  const notices = new Map();
  for (const entry of Array.isArray(safe.withheld) ? safe.withheld : []) {
    const label = cleanAudienceGroupText(entry?.label);
    const exclusion = cleanAudienceGroupText(entry?.exclusion);
    if (!label || isInternalPlanningLabel(label) || notices.has(label)) continue;
    if (entry?.relation === "self") {
      notices.set(label, `Strategy review: ${label} reads as an exclusion in the planner inputs and is not weighted; confirm whether it is excluded.`);
      continue;
    }
    const named = exclusion && !isInternalPlanningLabel(exclusion) ? `“${exclusion}”` : "an exclusion";
    if (entry?.relation === "within") {
      notices.set(label, `Strategy review: ${label} is part of ${named} in the planner inputs and is not weighted; confirm whether it is excluded.`);
      continue;
    }
    notices.set(label, `Strategy review: ${label} follows ${named} in a saved planner list and is not weighted; confirm whether it is excluded.`);
  }
  return [...notices.values()];
};

/*
 * The served model as the page shows it, or null when the section carries
 * none (a legacy artifact, or a backend that predates the model): the page
 * then renders the section as before.
 */
export const readPlanningWeights = (section, { audience = PLANNING_WEIGHTS_AUDIENCES.CLIENT } = {}) => {
  const model = section && typeof section === "object" && !Array.isArray(section) ? section.planningWeights : null;
  if (!model || typeof model !== "object" || model.contractVersion !== PLANNING_WEIGHTS_CONTRACT_VERSION) return null;
  const copy = PLANNING_WEIGHTS_EMPTY_COPY[audience] || PLANNING_WEIGHTS_EMPTY_COPY.client;
  const served = Array.isArray(model.rows) ? model.rows : [];
  const read = served.map(readPlanningWeightRow);
  const tenths = read.reduce((sum, row) => sum + (row ? Math.round(row.weight * 10) : 0), 0);
  // Any row the page cannot show, or weights that do not total 100.0: the
  // model is withheld whole, never drawn in part.
  if (read.some((row) => !row) || (read.length && tenths !== 1000)) {
    return { rows: [], groups: [], notices: [], maxWeight: 0, withheld: true, emptyCopy: copy.withheld };
  }
  const rows = read;
  const groups = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.category === row.category) last.rows.push(row);
    else groups.push({ category: row.category, label: row.categoryLabel, rows: [row] });
  }
  for (const group of groups) {
    group.showLabel = group.rows.length > 1 || group.rows[0].displayLabel !== group.label;
  }
  return {
    rows,
    groups,
    notices: [
      ...planningCoverageNotices(model.marketCoverage, audience),
      ...exclusionReviewNotices(model.exclusionReview, audience),
    ],
    maxWeight: rows.reduce((highest, row) => Math.max(highest, row.weight), 0),
    withheld: false,
    emptyCopy: copy[model.status] || copy.default,
  };
};

// The section's audience priorities for a context reader (the in-page
// assistant): the model's priorities when the section carries one, never its
// saved fragments; otherwise the saved cohorts as served.
export const audiencePriorityCohorts = (section) => {
  const model = readPlanningWeights(section);
  if (model) {
    return model.rows.map((row) => ({
      key: row.key,
      label: row.displayLabel,
      shareLabel: formatPlanningWeight(row.weight),
      rationale: row.members.join(", "),
    }));
  }
  return Array.isArray(section?.cohorts) ? section.cohorts : [];
};

export const formatPlanningPriorityCount = (count) => pluralCount(count, "planning priority", "planning priorities");

export const framePlanningWeightsSection = (planningWeights) => {
  const count = Array.isArray(planningWeights?.rows) ? planningWeights.rows.length : 0;
  return {
    mode: "planning",
    title: AUDIENCE_SECTION_FRAMING.planning.title,
    subtitle: AUDIENCE_SECTION_FRAMING.planning.subtitle,
    badge: formatPlanningPriorityCount(count),
    count,
    audienceCount: 0,
  };
};
